/**
 * Feed PostHog telemetry — web equivalents of the iOS feed-event hooks
 * (`useTrackFeedEvents`, `useTrackWordClicked`, `useTrackWordStatusChange`,
 * `useTrackPhraseHighlighted`, `useTrackPhraseSaved`, `useTrackExplainerEvents`,
 * `useTrackVideoPlayerViewed`, `useTrackLessonEvents` in `client-native`).
 *
 * Event names and property keys are 1-1 with iOS (snake_case). `platform` is
 * "web" here vs iOS "native" so the two clients can be told apart in PostHog.
 * One thin wrapper around `usePostHog().capture` so a missing PostHog client
 * (e.g. opted-out user) is a no-op rather than a crash.
 *
 * Source for word/phrase/status/explainer events in the feed is "video_feed"
 * (matches iOS, which passes "video_feed" for every feed-surface event).
 */
import { useCallback, useMemo, useRef } from "react";
import { usePostHog } from "posthog-js/react";

/**
 * Module-level feed session state (mirrors iOS `useTrackFeedEvents.tsx`, which
 * keeps the session start time + engagement counters as module singletons so
 * they survive across the multiple feed components that emit events within one
 * session). `feed_session_started` resets them; `feed_session_ended` and
 * `feed_listening_goal_reached` read them to attach the rich engagement payload.
 */
let feedSessionStartTime: number | null = null;
let sessionScrollCount = 0;
let sessionSwipeCount = 0;
let sessionLikeCount = 0;
let sessionUnlikeCount = 0;
let sessionVideoViewCount = 0;
let sessionVideoCompletionCount = 0;
let sessionVideoSkipCount = 0;

/** The session-engagement counters, as attached to session/goal events. */
function sessionCounterProps(): Record<string, number> {
  return {
    session_scroll_count: sessionScrollCount,
    session_swipe_count: sessionSwipeCount,
    session_like_count: sessionLikeCount,
    session_unlike_count: sessionUnlikeCount,
    session_video_view_count: sessionVideoViewCount,
    session_video_completion_count: sessionVideoCompletionCount,
    session_video_skip_count: sessionVideoSkipCount,
    net_likes_in_session: sessionLikeCount - sessionUnlikeCount,
  };
}

/** All feed-surface event names — kept 1-1 with the iOS PostHog hooks. */
export type FeedEvent =
  // feed-level (names already matched iOS)
  | "feed_session_started"
  | "feed_session_ended"
  | "feed_video_scrolled"
  | "feed_lesson_swiped"
  | "feed_carousel_swiped"
  | "feed_chapter_last_lesson_reached"
  | "feed_listening_goal_reached"
  | "feed_video_viewed"
  | "feed_video_completed"
  | "feed_video_skipped"
  | "feed_video_liked"
  | "feed_video_unliked"
  | "feed_video_player_viewed"
  | "feed_video_unavailable"
  | "feed_wordlist_opened"
  | "feed_wordlist_marked_known"
  | "feed_unlocked"
  | "feed_load_more"
  | "end_of_content_viewed"
  | "end_of_content_cta_clicked"
  // word / phrase / status / explainer / video — iOS names
  | "word_clicked"
  | "double_tap_word"
  | "word_moved_to_known"
  | "word_moved_to_learning"
  | "phrase_highlighted"
  | "phrase_saved"
  | "phrase_unsaved"
  | "ai_explainer_opened"
  | "ai_explainer_message_sent"
  | "video_player_viewed";

/** Source tag attached to every feed-originated word/phrase/status event. */
const FEED_SOURCE = "video_feed";
const PLATFORM = "web";

export function useTrackFeedEvents() {
  const posthog = usePostHog();
  // Guards the session lifecycle so feed_session_ended only fires for a session
  // this hook instance actually started (mirrors iOS hasSessionRef).
  const hasSessionRef = useRef(false);

  /** Low-level capture — never throws (opted-out / missing client = no-op). */
  const capture = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      try {
        posthog?.capture(event, properties);
      } catch {
        // Telemetry must never break the feed.
      }
    },
    [posthog],
  );

  // --- Session lifecycle (iOS trackFeedSessionStarted / Ended) -------------
  // Reset the engagement counters and stamp the start time, then emit
  // feed_session_started. Only starts if no session is already active.
  const trackFeedSessionStarted = useCallback(() => {
    if (feedSessionStartTime) return;
    feedSessionStartTime = Date.now();
    hasSessionRef.current = true;
    sessionScrollCount = 0;
    sessionSwipeCount = 0;
    sessionLikeCount = 0;
    sessionUnlikeCount = 0;
    sessionVideoViewCount = 0;
    sessionVideoCompletionCount = 0;
    sessionVideoSkipCount = 0;
    capture("feed_session_started");
  }, [capture]);

  // Compute the session duration + engagement/derived metrics and emit
  // feed_session_ended, then clear the session. Only ends a session this hook
  // instance owns.
  // iOS LearnFeedScreen passes (userStats.dayStreak, user.placementResult) →
  // emits streak_days + user_level (useTrackFeedEvents.tsx:104-121). Mirror the
  // arg order and property names exactly.
  const trackFeedSessionEnded = useCallback(
    (streakDays?: number, userLevel?: string) => {
      if (!feedSessionStartTime || !hasSessionRef.current) return;
      const sessionDuration = (Date.now() - feedSessionStartTime) / 1000;
      const netLikes = sessionLikeCount - sessionUnlikeCount;
      const completionRate =
        sessionVideoViewCount > 0
          ? (sessionVideoCompletionCount / sessionVideoViewCount) * 100
          : 0;
      const skipRate =
        sessionVideoViewCount > 0
          ? (sessionVideoSkipCount / sessionVideoViewCount) * 100
          : 0;
      capture("feed_session_ended", {
        session_duration_seconds: Math.round(sessionDuration),
        ...(streakDays !== undefined && { streak_days: streakDays }),
        ...(userLevel && { user_level: userLevel }),
        ...sessionCounterProps(),
        completion_rate_percent: Math.round(completionRate * 100) / 100,
        skip_rate_percent: Math.round(skipRate * 100) / 100,
        // Derived metrics for bucketing in PostHog (iOS parity).
        scroll_intensity:
          sessionScrollCount > 0 && sessionDuration > 0
            ? Math.round((sessionScrollCount / sessionDuration) * 60 * 100) / 100
            : 0,
        engagement_score:
          sessionVideoViewCount > 0
            ? Math.round((netLikes / sessionVideoViewCount) * 100) / 100
            : 0,
      });
      feedSessionStartTime = null;
      hasSessionRef.current = false;
    },
    [capture],
  );

  // Accepts the known FeedEvent literals as well as dynamic per-type activity
  // names (e.g. `mcq_activity_completed`), so the parameter is widened to string.
  // The two session lifecycle events are intercepted so callers that fire them
  // via the generic `trackEvent` still get the rich counter management/payload.
  const trackEvent = useCallback(
    (event: FeedEvent | string, properties?: Record<string, unknown>) => {
      if (event === "feed_session_started") {
        trackFeedSessionStarted();
        return;
      }
      if (event === "feed_session_ended") {
        trackFeedSessionEnded(
          typeof properties?.streak_days === "number"
            ? properties.streak_days
            : undefined,
          typeof properties?.user_level === "string"
            ? properties.user_level
            : undefined,
        );
        return;
      }
      capture(event, properties);
    },
    [capture, trackFeedSessionStarted, trackFeedSessionEnded],
  );

  // --- Video engagement counters (iOS trackFeedVideo{Viewed,Completed,…}) --
  const trackFeedVideoViewed = useCallback(
    (videoId: string | number, source?: string) => {
      sessionVideoViewCount++;
      capture("feed_video_viewed", {
        video_id: videoId,
        ...(source && { source }),
      });
    },
    [capture],
  );
  const trackFeedVideoCompleted = useCallback(
    (videoId: string | number, watchTime: number) => {
      sessionVideoCompletionCount++;
      capture("feed_video_completed", {
        video_id: videoId,
        watch_time: watchTime,
      });
    },
    [capture],
  );
  const trackFeedVideoSkipped = useCallback(
    (videoId: string | number, watchTime: number) => {
      sessionVideoSkipCount++;
      capture("feed_video_skipped", {
        video_id: videoId,
        watch_time: watchTime,
      });
    },
    [capture],
  );
  const trackFeedVideoLiked = useCallback(
    (chapterId: number) => {
      sessionLikeCount++;
      capture("feed_video_liked", { chapter_id: chapterId });
    },
    [capture],
  );
  const trackFeedVideoUnliked = useCallback(
    (chapterId: number) => {
      sessionUnlikeCount++;
      capture("feed_video_unliked", { chapter_id: chapterId });
    },
    [capture],
  );

  // --- Listening goal (iOS trackFeedListeningGoalReached) ------------------
  // Carries the session engagement counters so goal completion can be
  // correlated with in-session behavior.
  const trackFeedListeningGoalReached = useCallback(
    (
      listeningTimeSeconds: number,
      targetTimeSeconds: number,
      streakDays?: number,
      userLevel?: string,
    ) =>
      capture("feed_listening_goal_reached", {
        listening_time_seconds: listeningTimeSeconds,
        target_time_seconds: targetTimeSeconds,
        ...(streakDays !== undefined && { streak_days: streakDays }),
        ...(userLevel && { user_level: userLevel }),
        ...sessionCounterProps(),
      }),
    [capture],
  );

  // --- Word clicks (iOS useTrackWordClicked) ---
  const trackWordClicked = useCallback(
    () => trackEvent("word_clicked"),
    [trackEvent],
  );
  const trackDoubleTapWord = useCallback(
    () => trackEvent("double_tap_word"),
    [trackEvent],
  );

  // --- Word status change (iOS useTrackWordStatusChange) ---
  // `source` mirrors the iOS trailing optional argument (iOS lesson surfaces
  // pass "video_lesson", VideoNew.tsx:310-343); the feed default stays
  // "video_feed" so existing feed callers are unchanged.
  const trackWordMovedToKnown = useCallback(
    (wordId: string, wordText: string, source: string = FEED_SOURCE) =>
      trackEvent("word_moved_to_known", {
        word_id: wordId,
        word_text: wordText,
        platform: PLATFORM,
        source,
      }),
    [trackEvent],
  );
  const trackWordMovedToLearning = useCallback(
    (wordId: string, wordText: string, source: string = FEED_SOURCE) =>
      trackEvent("word_moved_to_learning", {
        word_id: wordId,
        word_text: wordText,
        platform: PLATFORM,
        source,
      }),
    [trackEvent],
  );

  // --- Phrase highlight / save (iOS useTrackPhraseHighlighted / Saved) ---
  const trackPhraseHighlighted = useCallback(
    (phraseText: string, wordCount: number, source: string = FEED_SOURCE) =>
      trackEvent("phrase_highlighted", {
        phrase_text: phraseText,
        word_count: wordCount,
        platform: PLATFORM,
        source,
      }),
    [trackEvent],
  );
  const trackPhraseSaved = useCallback(
    (isSaved: boolean, source: string = FEED_SOURCE) =>
      trackEvent(isSaved ? "phrase_saved" : "phrase_unsaved", {
        platform: PLATFORM,
        source,
      }),
    [trackEvent],
  );

  // --- AI explainer (iOS useTrackExplainerEvents) ---
  const trackExplainerOpened = useCallback(
    (
      word: string,
      translation: string,
      isPhrase: boolean,
      lessonId?: number,
      chapterId?: number,
    ) =>
      trackEvent("ai_explainer_opened", {
        word,
        translation,
        is_phrase: isPhrase,
        platform: PLATFORM,
        ...(lessonId !== undefined && { lesson_id: lessonId }),
        ...(chapterId !== undefined && { chapter_id: chapterId }),
      }),
    [trackEvent],
  );
  const trackExplainerMessageSent = useCallback(
    (messageCount: number, lessonId?: number, chapterId?: number) =>
      trackEvent("ai_explainer_message_sent", {
        message_count: messageCount,
        platform: PLATFORM,
        ...(lessonId !== undefined && { lesson_id: lessonId }),
        ...(chapterId !== undefined && { chapter_id: chapterId }),
      }),
    [trackEvent],
  );

  // --- Per-type activity events (iOS useTrackLessonEvents, dynamic names) ---
  const trackActivityCompleted = useCallback(
    (activityName: string, lessonId?: number, chapterId?: number) =>
      trackEvent(`${activityName}_activity_completed`, {
        activity_name: activityName,
        ...(lessonId !== undefined && { lesson_id: lessonId }),
        ...(chapterId !== undefined && { chapter_id: chapterId }),
      }),
    [trackEvent],
  );
  const trackActivityAttempt = useCallback(
    (
      activityName: string,
      isCorrect: boolean,
      lessonId?: number,
      chapterId?: number,
      attemptNumber?: number,
    ) =>
      trackEvent(`${activityName}_activity_attempt`, {
        activity_name: activityName,
        activity_type: "personalized",
        is_correct: isCorrect,
        // iOS sends attempt_number (1,2,3… per activity) + is_first_attempt.
        ...(attemptNumber !== undefined && {
          attempt_number: attemptNumber,
          is_first_attempt: attemptNumber === 1,
        }),
        ...(lessonId !== undefined && { lesson_id: lessonId }),
        ...(chapterId !== undefined && { chapter_id: chapterId }),
      }),
    [trackEvent],
  );

  // --- Vertical / horizontal navigation (iOS feed-level events) ---
  // Each navigation event bumps the relevant session counter and reports the
  // running cumulative total (iOS attaches cumulative_scroll_count /
  // cumulative_swipe_count to these events).
  const trackFeedVideoScrolled = useCallback(
    (
      chapterId: number,
      lessonId: number,
      scrollIndex: number,
      lessonIndex: number,
    ) => {
      sessionScrollCount++;
      capture("feed_video_scrolled", {
        chapter_id: chapterId,
        lesson_id: lessonId,
        scroll_index: scrollIndex,
        lesson_index: lessonIndex,
        cumulative_scroll_count: sessionScrollCount,
      });
    },
    [capture],
  );
  const trackFeedLessonSwiped = useCallback(
    (
      chapterId: number,
      lessonId: number,
      lessonIndex: number,
      direction: "left" | "right",
    ) => {
      sessionSwipeCount++;
      capture("feed_lesson_swiped", {
        chapter_id: chapterId,
        lesson_id: lessonId,
        lesson_index: lessonIndex,
        direction,
        cumulative_swipe_count: sessionSwipeCount,
      });
    },
    [capture],
  );
  const trackFeedChapterLastLessonReached = useCallback(
    (chapterId: number, lessonId: number, totalLessonsInChapter: number) =>
      capture("feed_chapter_last_lesson_reached", {
        chapter_id: chapterId,
        lesson_id: lessonId,
        total_lessons_in_chapter: totalLessonsInChapter,
        session_scroll_count: sessionScrollCount,
        session_swipe_count: sessionSwipeCount,
      }),
    [capture],
  );
  const trackFeedCarouselSwiped = useCallback(
    (
      chapterId: number,
      lessonId: number,
      contentType: "video" | "activity",
      activityType?: string,
      isAutoAdvance?: boolean,
    ) => {
      sessionSwipeCount++;
      capture("feed_carousel_swiped", {
        chapter_id: chapterId,
        lesson_id: lessonId,
        content_type: contentType,
        ...(activityType && { activity_type: activityType }),
        is_auto_advance: isAutoAdvance ?? false,
        cumulative_swipe_count: sessionSwipeCount,
      });
    },
    [capture],
  );

  // --- Video player viewed (iOS useTrackVideoPlayerViewed) ---
  const trackVideoPlayerViewed = useCallback(
    () => trackEvent("video_player_viewed"),
    [trackEvent],
  );

  return useMemo(
    () => ({
      trackEvent,
      trackFeedSessionStarted,
      trackFeedSessionEnded,
      trackFeedVideoViewed,
      trackFeedVideoCompleted,
      trackFeedVideoSkipped,
      trackFeedVideoLiked,
      trackFeedVideoUnliked,
      trackFeedListeningGoalReached,
      trackWordClicked,
      trackDoubleTapWord,
      trackWordMovedToKnown,
      trackWordMovedToLearning,
      trackPhraseHighlighted,
      trackPhraseSaved,
      trackExplainerOpened,
      trackExplainerMessageSent,
      trackActivityCompleted,
      trackActivityAttempt,
      trackFeedVideoScrolled,
      trackFeedLessonSwiped,
      trackFeedChapterLastLessonReached,
      trackFeedCarouselSwiped,
      trackVideoPlayerViewed,
    }),
    [
      trackEvent,
      trackFeedSessionStarted,
      trackFeedSessionEnded,
      trackFeedVideoViewed,
      trackFeedVideoCompleted,
      trackFeedVideoSkipped,
      trackFeedVideoLiked,
      trackFeedVideoUnliked,
      trackFeedListeningGoalReached,
      trackWordClicked,
      trackDoubleTapWord,
      trackWordMovedToKnown,
      trackWordMovedToLearning,
      trackPhraseHighlighted,
      trackPhraseSaved,
      trackExplainerOpened,
      trackExplainerMessageSent,
      trackActivityCompleted,
      trackActivityAttempt,
      trackFeedVideoScrolled,
      trackFeedLessonSwiped,
      trackFeedChapterLastLessonReached,
      trackFeedCarouselSwiped,
      trackVideoPlayerViewed,
    ],
  );
}
